import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";

const ComplianceReportModal = ({ open, setOpen, data, get_survey_structure, loading }) => {
  const [selected, setSelected] = useState(null);
  const [options, setOptions] = useState([]);
  const [completionData, setCompletionData] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const buttonRef = useRef(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0, width: 0, maxHeight: 200, placement: "below" });

  useEffect(() => {
    if (get_survey_structure) {
      setOptions(
        get_survey_structure?.[0]?.categories?.filter(
        (f) => f?.visibility === "public" && !f?.demographic && !f?.hidden
        )
      );
    }
  }, [get_survey_structure]);

  useEffect(() => {
    let completion = [];
    selected?.options.map((option) => {
      const req = data.filter(
        (f) =>
          f.categories?.find((c) => c.name_id === selected.id)?.value_id === option.id
      );
      const badWayReq = data.filter(
        (f) =>
          f.categories?.find((c) => c.name_id === selected.id)?.value === option.name
      );
      const total = badWayReq.length;
      const completed = badWayReq.filter((f) => f.status === "completed").length;
      if (total > 0) {
        completion.push({
          name: option.name,
          total,
          completed,
        });
      }
    });
    setCompletionData(completion);
  }, [selected, data]);

  const getCompletion = (a, b) => {
    return b.completed / b.total - a.completed / a.total;
  };

  useEffect(() => {
    const handle = () => {
      if (!dropdownOpen || !buttonRef.current) return;
      const rect = buttonRef.current.getBoundingClientRect();
      const gap = 4;
      const desiredHeight = 200;
      const spaceBelow = window.innerHeight - rect.bottom - gap;
      const spaceAbove = rect.top - gap;
      const placeAbove = spaceBelow < Math.min(desiredHeight, spaceAbove);
      const maxHeight = placeAbove ? Math.min(desiredHeight, spaceAbove) : Math.min(desiredHeight, spaceBelow);
      setMenuPos({
        top: placeAbove ? rect.top - maxHeight - gap : rect.bottom + gap,
        left: rect.left,
        width: rect.width,
        maxHeight,
        placement: placeAbove ? "above" : "below",
      });
    };
    const closeOnEscape = (e) => {
      if (e.key === "Escape") setDropdownOpen(false);
    };
    window.addEventListener("resize", handle);
    window.addEventListener("scroll", handle, true);
    window.addEventListener("keydown", closeOnEscape);
    handle();
    return () => {
      window.removeEventListener("resize", handle);
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [dropdownOpen]);

  useEffect(() => {
    if (!open) setDropdownOpen(false);
  }, [open]);

  if (!open) return null;

  return (
    <ModalOverlay onClick={() => setOpen(false)}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Participation by category</ModalTitle>
          <CloseButton onClick={() => setOpen(false)}>×</CloseButton>
        </ModalHeader>

        <ModalContent>
          {loading && (
            <LoadingOverlay>
              <LoadingSpinner />
              <LoadingText>Loading categories...</LoadingText>
            </LoadingOverlay>
          )}
          <DropdownSection style={{ opacity: loading ? 0.3 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            <DropdownLabel>Select Category</DropdownLabel>
            <DropdownContainer>
              <DropdownButton
                ref={buttonRef}
                onClick={() => {
                  const willOpen = !dropdownOpen;
                  setDropdownOpen(willOpen);
                  if (willOpen && buttonRef.current) {
                    const rect = buttonRef.current.getBoundingClientRect();
                    const gap = 4;
                    const desiredHeight = 200;
                    const spaceBelow = window.innerHeight - rect.bottom - gap;
                    const spaceAbove = rect.top - gap;
                    const placeAbove = spaceBelow < Math.min(desiredHeight, spaceAbove);
                    const maxHeight = placeAbove ? Math.min(desiredHeight, spaceAbove) : Math.min(desiredHeight, spaceBelow);
                    setMenuPos({
                      top: placeAbove ? rect.top - maxHeight - gap : rect.bottom + gap,
                      left: rect.left,
                      width: rect.width,
                      maxHeight,
                      placement: placeAbove ? "above" : "below",
                    });
                  }
                }}
                hasSelection={!!selected}
              >
                {selected ? selected.name : "Choose a category..."}
                <DropdownIcon dropdownOpen={dropdownOpen}>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </DropdownIcon>
              </DropdownButton>

              {dropdownOpen &&
                createPortal(
                  <DropdownMenuPortal
                    style={{
                      top: menuPos.top,
                      left: menuPos.left,
                      width: menuPos.width,
                      maxHeight: menuPos.maxHeight,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    {options.map((option, index) => (
                      <DropdownItem
                        key={index}
                        onClick={() => {
                          setSelected(option);
                          setDropdownOpen(false);
                        }}
                      >
                        {option.name}
                      </DropdownItem>
                    ))}
                  </DropdownMenuPortal>,
                  document.body
                )}
            </DropdownContainer>
          </DropdownSection>

          {selected && completionData.length > 0 && !loading && (
            <TableSection>
              <Table>
                <TableHeader>
                  <TableHeaderRow>
                    <TableHeaderCell>Category</TableHeaderCell>
                    <TableHeaderCell>Completion</TableHeaderCell>
                    <TableHeaderCell>Percentage</TableHeaderCell>
                  </TableHeaderRow>
                </TableHeader>
                <TableBody>
                  {completionData
                    .sort((a, b) => getCompletion(a, b))
                    .map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>
                          {item.completed}/{item.total}
                        </TableCell>
                        <TableCell>
                          <PercentageCell>
                            {Math.round((item.completed / item.total) * 100)}%
                          </PercentageCell>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableSection>
          )}

          {selected && completionData.length === 0 && !loading && (
            <EmptyState>
              <EmptyIcon>📊</EmptyIcon>
              <EmptyTitle>No Data Available</EmptyTitle>
              <EmptyText>
                No completion data found for the selected category.
              </EmptyText>
            </EmptyState>
          )}
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

export default ComplianceReportModal;

// Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.2s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 12px;
  width: 90%;
  max-width: 700px;
  max-height: 80vh;
  overflow: visible;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  animation: slideIn 0.3s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #e0e0e0;
  background: #f8f9fa;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
`;

const ModalTitle = styled.h2`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 20px;
  color: #414141;
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border: none;
  background: transparent;
  font-size: 24px;
  color: #7e7e7e;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.2s;

  &:hover {
    background: #f0f0f0;
    color: #414141;
  }
`;

const ModalContent = styled.div`
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: visible;
  position: relative;
  min-height: 300px;
`;

const DropdownSection = styled.div`
  margin-bottom: 24px;
  overflow: visible;
  position: relative;
`;

const DropdownLabel = styled.label`
  display: block;
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
  margin-bottom: 8px;
`;

const DropdownContainer = styled.div`
  position: relative;
`;

const DropdownButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-family: "Poppins";
  font-size: 14px;
  color: ${(props) => (props.hasSelection ? "#414141" : "#7e7e7e")};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    border-color: #476dfa;
  }

  &:focus {
    outline: none;
    border-color: #476dfa;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.1);
  }
`;

const DropdownIcon = styled.div`
  color: #7e7e7e;
  transition: transform 0.2s;
  transform: ${(props) =>
    props.dropdownOpen ? "rotate(180deg)" : "rotate(0deg)"};
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 10001;
  max-height: 200px;
  overflow-y: auto;
`;

const DropdownMenuPortal = styled.div`
  position: fixed;
  z-index: 10002; /* above ModalOverlay (10000) */
  margin-top: 0;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  overflow-y: auto;
`;

const DropdownItem = styled.div`
  padding: 12px 16px;
  font-family: "Poppins";
  font-size: 14px;
  color: #414141;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #f8f9fa;
  }

  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }

  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const TableSection = styled.div`
  background: white;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: #f8f9fa;
`;

const TableHeaderRow = styled.tr``;

const TableHeaderCell = styled.th`
  padding: 16px 20px;
  text-align: left;
  font-family: "Poppins";
  font-weight: 600;
  font-size: 14px;
  color: #414141;
  border-bottom: 1px solid #e0e0e0;

  &:first-child {
    width: 50%;
  }
  &:nth-child(2) {
    width: 25%;
  }
  &:last-child {
    width: 25%;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  transition: background-color 0.2s;

  &:hover {
    background: #f0f4ff;
  }
`;

const TableCell = styled.td`
  padding: 16px 20px;
  font-family: "Poppins";
  font-size: 14px;
  color: #414141;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }
`;

const PercentageCell = styled.div`
  font-weight: 600;
  color: #476dfa;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const EmptyTitle = styled.h3`
  font-family: "Poppins";
  font-weight: 600;
  font-size: 18px;
  color: #414141;
  margin: 0 0 8px 0;
`;

const EmptyText = styled.p`
  font-family: "Poppins";
  font-size: 14px;
  color: #7e7e7e;
  margin: 0;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #476dfa;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;
